<template>
  <div class="flex flex-col h-full w-full relative">
    <div class="flex items-center space-x-4 text-xl font-medium">
      <ArrowLeft v-if="selected_option" class="w-8 h-8 mr-4 cursor-pointer hover:bg-gray-100 p-1 rounded-md" @click="clearSelectedOption" />
      {{ $t("Take Quick Action") }} {{ selected_option ? "-" : "" }} {{ selected_option?.name }}
    </div>

    <div v-if="!selected_option" class="w-full flex flex-col items-center mt-10 gap-2">
      <div v-for="action in quick_ai_actions" :key="action.name" class="btn btn-feature" @click="selectOption(action)">
        <component :is="action.icon" class="mr-4" />
        {{ action.name }}
      </div>
    </div>
    <div v-else class="h-full w-full overflow-x-hidden overflow-y-auto">
      <div v-if="loading" class="w-full flex justify-center items-center mt-8">
        <span class="loading loading-spinner loading-md" />
      </div>

      <div v-else-if="record_error" class="w-full flex justify-center items-center mt-8">
        <div class="text-red-500">
          {{ record_error }}
        </div>
      </div>

      <div v-else-if="!insights || !insights[selected_option.value]" class="w-full flex justify-center items-center mt-8">
        <div class="text-gray-500">
          {{ $t("No AI insights available for") }} {{ selected_option?.name }}.
          {{ $t("Please try again later or select a different action") }}
        </div>
      </div>

      <div v-else class="w-full flex flex-col items-center">
        <div class="w-full bg-blue-50/50 mt-4 p-4 rounded-lg">
          <div v-for="[label, content] in Object.entries(insights[selected_option.value])" :key="label" class="gap-2 mb-2">
            <span class="font-semibold capitalize">{{ label }}:</span>

            <!-- Check if content is an array -->
            <div v-if="Array.isArray(content) && label != 'tasks'" class="prose">
              <ul class="ml-4 list-disc">
                <li v-for="(item, index) in content" :key="index">
                  {{ item }}
                </li>
              </ul>
            </div>
            <div v-else-if="Array.isArray(content) && label == 'tasks'">
              <ul class="list-disc">
                <TaskRowCard :tasks="content.values()" :current-company="currentCompany" />
              </ul>
            </div>

            <!-- Check if content is an object -->
            <div v-else-if="typeof content === 'object'" class="prose">
              <ul class="ml-4 list-disc">
                <li v-for="(item_key, item_value) in content" :key="item_key">
                  {{ item_key.title }}: {{ item_value }}
                </li>
              </ul>
            </div>

            <!-- Fallback for other types of content -->
            <div v-else>
              {{ content }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AIIcon from "@/components/shared/icons/AIIcon.vue"
import TaskRowCard from "@/components/shared/TaskRowCard.vue"
import { useLayoutItemSharedLogic } from "@/components/shared/pages/useLayoutItemSharedLogic.js"
import { ArrowLeft } from "lucide-vue-next"
import dataService from "@/services/data.service"

export default {
  components: { AIIcon, TaskRowCard, ArrowLeft },
  props: {
    linkedRecord: String,
    connectTo: String,
    pageReference: { type: String, required: true },
    currentCompany: {
      type: Object,
      default: () => {},
      required: false
    }
  },
  data() {
    return {
      component_record: null,
      loading: false,
      record_error: null,
      selected_option: null,
      insights: null,
      quick_ai_actions: [
        { name: this.$t("Research"), value: "research_suggestion", icon: "AIIcon" },
        { name: this.$t("Pre-Call Debrief"), value: "pre_call_debrief", icon: "AIIcon" },
        { name: this.$t("Suggested Tasks"), value: "suggested_tasks", icon: "AIIcon" }
      ]
    }
  },
  created() {
    const { loading, record_error, component_record } = useLayoutItemSharedLogic(this.$props, this.postGetComponentRecord)
    this.loading = loading
    this.record_error = record_error
    this.component_record = component_record
  },
  methods: {
    postGetComponentRecord() {
      this.getInsights()
    },
    getInsights() {
      this.loading = true
      this.record_error = null
      if (!this.component_record?.id) {
        this.record_error = this.$t("Unable to analyze: No record selected. Please select a record first")
        this.loading = false
        return
      }

      dataService
        .getRecordInsights(this.component_record.id)
        .then((res) => {
          this.insights = res.data
        })
        .catch((error) => {
          this.record_error = this.$t("Unable to generate AI insights at the moment. Please try again in a few minutes")
          console.error("Error fetching insights:", error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    selectOption(action) {
      this.selected_option = action
    },
    clearSelectedOption() {
      this.selected_option = null
    }
  }
}
</script>
